<template>
  <Information v-if="hasInfo" class="text-gray-500">
    <template #trigger>
      <InformationCircleIcon class="h-5 w-5" @mouseenter="handleHover" />
    </template>
    <template #modal>
      <div class="space-y-2">
        <div v-for="(line, index) in prependInfo" :key="index">
          {{ line }}
        </div>
        <div v-if="comparisonText">
          {{ comparisonText }}
        </div>
      </div>
    </template>
  </Information>
</template>

<script lang="ts" setup>
import Information from '@/components/Information.vue';
import { InformationCircleIcon } from '@heroicons/vue/24/outline';
import { shuffle } from 'lodash';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

/** Fun hover comparisons — first value divides euros, each next one divides the previous result */
const FUN_COMPARISONS = {
  bigMac: {
    count: 5.99,
    marathons: 2500 / 550,
  },
  euroCoinTower: {
    heightMeters: 428.67,
    cathedrals: 157,
  },
  espresso: {
    count: 1.5,
    dailyDoses: 400 / 63,
  },
  oktoberfestMass: {
    count: 15,
    bathtubs: 150,
  },
  doner: {
    count: 6.5,
    spits: 300,
  },
  deutschlandTicket: {
    months: 63,
    years: 12,
  },
  spezi: {
    liters: 1.65,
    truckloads: 120_000,
  },
  spotify: {
    months: 12.99,
    years: 12,
  },
} as const;

type FunComparisonKey = keyof typeof FUN_COMPARISONS;

const COMPARISON_KEYS = Object.keys(FUN_COMPARISONS) as FunComparisonKey[];

const props = defineProps<{
  value: number;
  prependInfo?: string[];
}>();

const { t, n } = useI18n();

const hasInfo = computed(() => (props.prependInfo?.length ?? 0) > 0 || props.value > 0);

function handleHover() {
  if (props.value > 0) {
    pickComparison();
  }
}
const comparisonKey = ref<FunComparisonKey | null>(null);
const remainingKeys = ref<FunComparisonKey[]>(shuffle([...COMPARISON_KEYS]));

function pickComparison() {
  if (props.value <= 0) {
    return;
  }

  if (!remainingKeys.value.length) {
    remainingKeys.value = shuffle([...COMPARISON_KEYS]);
  }

  comparisonKey.value = remainingKeys.value.shift()!;
}

const comparisonText = computed(() => {
  if (!comparisonKey.value || props.value <= 0) {
    return null;
  }

  let current = props.value;
  const formattedValues = Object.fromEntries(
    Object.entries(FUN_COMPARISONS[comparisonKey.value]).map(([key, divisor]) => {
      current /= divisor;
      return [key, n(current, { maximumFractionDigits: 2 })];
    }),
  );

  return t(`funComparisons.${comparisonKey.value}`, formattedValues);
});
</script>

<i18n lang="json">
{
  "en": {
    "funComparisons": {
      "bigMac": "That's {count} Big Macs — enough calories for {marathons} marathons. Enjoy your meal!",
      "euroCoinTower": "As a tower of €1 coins, that would be {heightMeters} m high. Equivalent to {cathedrals} Cologne Cathedrals.",
      "espresso": "That's {count} espressos — {dailyDoses} times the recommended daily dose. At your own risk.",
      "oktoberfestMass": "That's {count} Oktoberfest Maß — enough beer to fill {bathtubs} bathtubs. Prost!",
      "doner": "That's {count} Döners — or {spits} complete vertical spits. Mahlzeit!",
      "deutschlandTicket": "That's {months} months of Deutschland-Ticket — or {years} years of unlimited travel.",
      "spezi": "That's {liters} liters of Spezi — or {truckloads} truckloads.",
      "spotify": "That's {months} months of Spotify Premium — or {years} years of ad-free vibes."
    }
  },
  "de": {
    "funComparisons": {
      "bigMac": "Das entspricht {count} Big Macs – genug Kalorien für {marathons} Marathons. Guten Appetit!",
      "euroCoinTower": "Als Turm aus 1-€-Münzen wäre das {heightMeters} m hoch. Das entspricht {cathedrals} Kölner Domen.",
      "espresso": "Das entspricht {count} Espressi – das {dailyDoses}-fache der empfohlenen Tagesdosis. Schlaf ist überbewertet!",
      "oktoberfestMass": "Das entspricht {count} Oktoberfest-Maß – genug Bier, um {bathtubs} Badewannen zu füllen. Prost!",
      "doner": "Das entspricht {count} Dönern – oder {spits} komplette Drehspieße. Mahlzeit!",
      "deutschlandTicket": "Das entspricht {months} Monaten Deutschland-Ticket – oder {years} Jahren bundesweit unterwegs.",
      "spezi": "Das entspricht {liters} Litern Spezi – {truckloads} LKW-Ladungen.",
      "spotify": "Das entspricht {months} Monaten Spotify Premium – oder {years} Jahren ohne Werbung."
    }
  },
  "es": {
    "funComparisons": {
      "bigMac": "Eso son {count} Big Macs: ¡calorías para {marathons} maratones. ¡Buen provecho!",
      "euroCoinTower": "Como torre de monedas de 1 €, mediría {heightMeters} m. Equivale a {cathedrals} catedrales de Colonia.",
      "espresso": "Eso son {count} espressos: {dailyDoses} veces la dosis diaria recomendada. Bajo tu propio riesgo.",
      "oktoberfestMass": "Eso son {count} Maß de Oktoberfest: ¡cerveza para llenar {bathtubs} bañeras! ¡Prost!",
      "doner": "Eso son {count} döner kebabs, o {spits} broches enteros. ¡Buen provecho!",
      "deutschlandTicket": "Eso son {months} meses de Deutschland-Ticket, o {years} años viajando por Alemania.",
      "spezi": "Eso son {liters} litros de Spezi, o {truckloads} camiones articulados.",
      "spotify": "Eso son {months} meses de Spotify Premium, o {years} años sin anuncios."
    }
  }
}
</i18n>
